import { effectsBus } from './index';

/**
 * Recorded sound effects, the second exception to runtime synthesis.
 *
 * The voice pack was the first, because a voice cannot be synthesised. These
 * are the handful of moments where a real recording earns its bytes: an army
 * sent to war deserves a horn, not a filtered noise burst. Same discipline as
 * the voices: files are discovered at build time, a missing file means the
 * moment is silent rather than broken, and nothing may assume presence.
 *
 * Add files as `assets/audio/effects/<name>.mp3`; the basename is the key.
 */
const FILES = import.meta.glob('../assets/audio/effects/*.mp3', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const byName = new Map<string, string>();
for (const [path, url] of Object.entries(FILES)) {
  const name = path.split('/').pop()!.replace(/\.mp3$/i, '');
  byName.set(name, url);
}

const decoded = new Map<string, AudioBuffer>();

export function hasEffect(name: string): boolean {
  return byName.has(name);
}

export async function playEffect(name: string): Promise<void> {
  const bus = effectsBus();
  const url = byName.get(name);
  if (!bus || !url) return;
  try {
    let buffer = decoded.get(name);
    if (!buffer) {
      const bytes = await (await fetch(url)).arrayBuffer();
      buffer = await bus.ctx.decodeAudioData(bytes);
      decoded.set(name, buffer);
    }
    const source = bus.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(bus.out);
    source.start();
  } catch {
    // A failed sound must never take an interaction down with it.
  }
}
